/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import CreditOfferCard from "@components/CreditOfferCard";
import { RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import { ApiError } from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import { useLazyGetOffersQuery, usePostCloneApplicationMutation } from "@api/marketPlaceApis";
import { setSelectedOffer } from "@store/slices/marketPlaceSlice";
import { LoanApplicationOfferResponse, PayloadRequestColoneApplication } from "@interfaces/MarketPlace";
import { useLazyGetCuentasByUsernameQuery } from "@api/account";
import { signOut } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";

const OfertaSeleccionadaCompany: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const company = useSelector((state: RootState) => state.register.company);
  const offer = useSelector((state: RootState) => state.marketPalce.offers);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getOffersMarket] = useLazyGetOffersQuery();
  const [getAccountByUsername] = useLazyGetCuentasByUsernameQuery();
  const [postCloneApplication] = usePostCloneApplicationMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [offerSelected, setOfferSelected] = useState<LoanApplicationOfferResponse | null>(null);
  const [hasOffer, setHasOffer] = useState<boolean>(true);
  const [open, setOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);

  const closeModal = () => {
    setOpen(false);
  };

  const handleClick = async () => {
    await signOut();
    navigate("/login");
  };

  const handleSelectedOffer = async () => {
    try {
      setIsLoading(true);
      setIsDisabled(true);
      if (!offerSelected) return;
      const payload: PayloadRequestColoneApplication = {
        user_id: String(accountUser.id),
        transfer_company_id: offerSelected.financial_company.company_id || "",
        user_name: accountUser.email,
      };
      const responseUsers = await getAccountByUsername({ username: accountUser.email }).unwrap();

      const companyId = offerSelected.financial_company.company_id;
      const hasCuenta = (responseUsers ?? []).some((cuenta) => cuenta.company_id === companyId);

      await postCloneApplication({ application_id: application.id, data: payload }).unwrap();
      setHasAccount(hasCuenta);
      setOpen(true);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "OfertaSeleccionadaCompany-handleSelectedOffer",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const textDialog = (hasAccount: boolean) => {
    if (hasAccount) {
      return (
        <>
          <Typography
            sx={{ mb: 2 }}
            variant={"body1"}
            fontWeight={400}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            Detectamos que ya tienes una cuenta con Finbe ABC, ingresa con tu usuario y contraseña en el sitio de la
            financiera para continuar con tu solicitud.
          </Typography>
        </>
      );
    }
    return (
      <>
        <Typography
          sx={{ mb: 2 }}
          variant={"body1"}
          fontWeight={400}
          color={theme?.palette?.primary?.dark}
          textAlign={"center"}
        >
          A continuación, recibirás al correo <span style={{ fontWeight: 600 }}>{accountUser.email}</span> la
          información necesaria para concluir tu proceso de formalización.
        </Typography>
      </>
    );
  };
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const responseOffers = await getOffersMarket({ id_sol: application.id }).unwrap();

        const selected: LoanApplicationOfferResponse[] = responseOffers.filter((of) => of?.accept);
        if (selected.length > 0) {
          setOfferSelected(selected[0]);
          dispatch(setSelectedOffer(selected[0]));
          setHasOffer(true);
        } else {
          setHasOffer(false);
        }
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <Typography
          sx={{ mb: 1 }}
          variant={isMobileOrTablet ? "h4" : "h3"}
          fontWeight={600}
          color={theme?.palette?.primary?.main}
          textAlign={"center"}
        >
          ¡Tenemos buenas noticias! <br />
          {company.name}
        </Typography>

        <Box mr={3} ml={3}>
          <Typography
            sx={{ mb: 2 }}
            variant={"body1"}
            fontWeight={300}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            Esta oferta se completará a través del flujo digital de{" "}
            <span style={{ fontWeight: 600 }}>{offer?.financial_company?.name || "la financiera"}</span>, <br />
            operado en la plataforma de Lendia - PyMio.
          </Typography>

          <Typography
            sx={{ mb: 3, mx: 4 }}
            variant={"body1"}
            fontWeight={300}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            Al continuar,{" "}
            <span style={{ fontWeight: 600 }}>serás redirigido para finalizar tu proceso de forma rápida y segura</span>
            , con el mismo equipo y acompañamiento que te ha atendido hasta ahora.
          </Typography>
        </Box>

        <Box
          mt={6}
          sx={{
            boxShadow: "8px 4px 20px 0px #58585840",
            borderRadius: 10,
            p: "10px 0", // paddingTop y paddingBottom juntos
            maxWidth: 500,
            margin: "0 auto", // centra horizontalmente
            display: "flex",
            justifyContent: "center",
          }}
        >
          {hasOffer ? (
            <>
              <CreditOfferCard key={offer?.id} offer={offer} offers_length={0} showButton={false} />
            </>
          ) : (
            <Typography variant="body1">No se encontró una oferta seleccionada.</Typography>
          )}
        </Box>
        <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 4} mb={1}>
          <Button
            variant="default"
            onClick={handleSelectedOffer}
            sx={{ width: "130px" }}
            disabled={isLoading || !offerSelected || isDisabled}
          >
            Continuar
          </Button>
        </Box>

        <Dialog
          open={open}
          onClose={(_event, reason) => {
            if (reason !== "backdropClick") {
              closeModal();
            }
          }}
        >
          <DialogTitle sx={{ textAlign: "center", color: theme?.palette?.primary?.main }}>
            ¡Ya casi terminamos!
          </DialogTitle>
          <DialogContent>
            <Typography component="div" variant="body1" color={theme?.palette?.primary?.dark} align="center" mb={3}>
              {textDialog(hasAccount)}
            </Typography>

            <Box display="flex" justifyContent="center">
              <Button variant="default" sx={{ width: "130px" }} onClick={handleClick}>
                Salir
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default OfertaSeleccionadaCompany;
